import { useCallback, useEffect, useMemo, useRef } from 'react';

import { bind, type UnbindFn } from 'bind-event-listener';

const PRELOAD_MAX_AGE = 5 * 60 * 1000;
const TIME_TO_INTENT = 200;

export type OnLoad<T> = (obj: { dispose: () => void; reference: T; preloaded: boolean }) => void;

export type UsePreloadRefProps<T> = {
	load: () => T;
	onLoad: OnLoad<T>;
	/** Called when a hover preload request is dispatched, before the popup opens. */
	onPreload?: () => void;
	/**
	 * How long (ms) the pointer must rest on the trigger before preloading. Raise it when the
	 * preloaded request is expensive enough that speculative hovers are not worth serving.
	 * Defaults to 200ms.
	 */
	timeToIntent?: number;
	/**
	 * Keep a preload that has already fired when the pointer leaves the trigger, so the next hover
	 * or open reuses it. Only a pending preload is cancelled. Defaults to false.
	 */
	keepPreloadOnLeave?: boolean;
};

export function usePreloadRef<T extends { dispose: () => void }>({
	load,
	onLoad,
	onPreload,
	timeToIntent = TIME_TO_INTENT,
	keepPreloadOnLeave = false,
}: UsePreloadRefProps<T>): ((element: HTMLElement | null) => void) & {
	loadAndOpen: () => void;
} {
	const nodeRef = useRef<HTMLElement | null>(null);
	const eventListeners = useRef<UnbindFn[]>([]);

	const { loadAndOpen, preloadReference, cancelPreload, cancelPreloadIntent } = useMemo(() => {
		const request: {
			load?: { dispose: () => void; reference: T };
			preload?: { dispose: () => void; reference: T };
			preloadIntentTimeout?: ReturnType<typeof setTimeout>;
			preloadMaxAgeTimeout?: ReturnType<typeof setTimeout>;
		} = {};

		const cancelPreloadIntent = () => {
			if (request.preloadIntentTimeout != null) {
				clearTimeout(request.preloadIntentTimeout);
				delete request.preloadIntentTimeout;
			}
		};

		const clearPreloadTimeouts = () => {
			cancelPreloadIntent();

			if (request.preloadMaxAgeTimeout != null) {
				clearTimeout(request.preloadMaxAgeTimeout);
				delete request.preloadMaxAgeTimeout;
			}
		};

		const cancelPreload = () => {
			clearPreloadTimeouts();
			request.preload?.dispose();
		};

		const loadReference = () => {
			if (request.load) {
				return request.load;
			}

			const reference = request.preload?.reference ?? load();
			if (request.preload) {
				delete request.preload;
			}

			request.load = {
				reference,
				dispose() {
					reference.dispose();
					delete request.load;
				},
			};

			return request.load;
		};

		const loadAndOpen = () => {
			clearPreloadTimeouts();
			const preloaded = !request.load && request.preload != null;
			onLoad({ ...loadReference(), preloaded });
		};

		const preloadReference = () => {
			if (request.preloadIntentTimeout || request.preload) {
				return;
			}

			request.preloadIntentTimeout = setTimeout(() => {
				delete request.preloadIntentTimeout;

				const reference = load();

				request.preload = {
					reference,
					dispose() {
						reference.dispose();
						delete request.preload;
					},
				};

				request.preloadMaxAgeTimeout = setTimeout(() => {
					delete request.preloadMaxAgeTimeout;
					request.preload?.dispose();
				}, PRELOAD_MAX_AGE);

				onPreload?.();
			}, timeToIntent);
		};

		return {
			loadAndOpen,
			preloadReference,
			cancelPreload,
			cancelPreloadIntent,
		};
	}, [load, onLoad, onPreload, timeToIntent]);

	const cleanUpEventListeners = useCallback(() => {
		eventListeners.current.forEach((unbind) => {
			unbind();
		});
		eventListeners.current = [];
	}, []);

	const addEventListener = useCallback(
		(element: HTMLElement, type: string, callback: EventListener) => {
			const unbind = bind(element, {
				type,
				listener: callback,
			});

			eventListeners.current.push(unbind);
		},
		[],
	);

	const refCallback = useCallback(
		(element: HTMLElement | null) => {
			if (element !== nodeRef.current) {
				if (nodeRef.current) {
					cancelPreload();
					cleanUpEventListeners();
				}
				if (element) {
					addEventListener(element, 'mouseenter', preloadReference);
					addEventListener(
						element,
						'mouseleave',
						keepPreloadOnLeave ? cancelPreloadIntent : cancelPreload,
					);
				}
				nodeRef.current = element;
			}
		},
		[
			preloadReference,
			cancelPreload,
			cancelPreloadIntent,
			keepPreloadOnLeave,
			addEventListener,
			cleanUpEventListeners,
		],
	);

	useEffect(() => {
		return () => {
			cleanUpEventListeners();
		};
	}, [cleanUpEventListeners]);

	return Object.assign(refCallback, {
		loadAndOpen,
	});
}
